import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { getTourStepsForRole, TourStep } from '../data/tourSteps'

interface TourContextValue {
  isTourActive: boolean
  currentStepIndex: number
  currentStep: TourStep | null
  totalSteps: number
  startTour: () => void
  nextStep: () => void
  prevStep: () => void
  skipTour: () => void
  finishTour: () => void
  setSidebarTab: (tabId: string) => void
  setAdminSection: (section: string) => void
  navigateToAdmin: () => void
  navigateToApp: () => void
  registerSidebarTabSetter: (fn: (tabId: string) => void) => void
  registerAdminSectionSetter: (fn: (section: string) => void) => void
}

const TourContext = createContext<TourContextValue | null>(null)

export function TourProvider({ children }: { children: React.ReactNode }) {
  const { user, completeOnboarding } = useAuth()
  const navigate = useNavigate()
  const [isTourActive, setIsTourActive] = useState(false)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [steps, setSteps] = useState<TourStep[]>([])

  const sidebarTabSetterRef = useRef<((tabId: string) => void) | null>(null)
  const adminSectionSetterRef = useRef<((section: string) => void) | null>(null)

  const startTour = useCallback(() => {
    const userTypeForTour = (() => {
      try {
        const stored = localStorage.getItem('allocatiq_user_type')
        if (stored) return stored as any
      } catch { /* ignore */ }
      return 'viewer'
    })()
    const userSteps = getTourStepsForRole(userTypeForTour)
    setSteps(userSteps)
    setCurrentStepIndex(0)
    setIsTourActive(true)
  }, [])

  const nextStep = useCallback(() => {
    setCurrentStepIndex(prev => {
      if (prev + 1 >= steps.length) {
        setIsTourActive(false)
        completeOnboarding()
        return prev
      }
      return prev + 1
    })
  }, [steps.length, completeOnboarding])

  const prevStep = useCallback(() => {
    setCurrentStepIndex(prev => Math.max(0, prev - 1))
  }, [])

  const skipTour = useCallback(() => {
    setIsTourActive(false)
    completeOnboarding()
  }, [completeOnboarding])

  const finishTour = useCallback(() => {
    setIsTourActive(false)
    completeOnboarding()
  }, [completeOnboarding])

  const setSidebarTab = useCallback((tabId: string) => {
    sidebarTabSetterRef.current?.(tabId)
  }, [])

  const setAdminSection = useCallback((section: string) => {
    adminSectionSetterRef.current?.(section)
  }, [])

  const navigateToAdmin = useCallback(() => {
    navigate('/admin')
  }, [navigate])

  const navigateToApp = useCallback(() => {
    navigate('/app')
  }, [navigate])

  const registerSidebarTabSetter = useCallback((fn: (tabId: string) => void) => {
    sidebarTabSetterRef.current = fn
  }, [])

  const registerAdminSectionSetter = useCallback((fn: (section: string) => void) => {
    adminSectionSetterRef.current = fn
  }, [])

  const currentStep = isTourActive && steps.length > 0 ? steps[currentStepIndex] : null

  // Track how many times we've attempted to apply the current step's target.
  // This handles the race where AdminPanel/AppShell haven't registered their
  // section/tab setter yet when the tour navigation effect first runs.
  const applyAttemptRef = useRef(0)

  useEffect(() => {
    if (!isTourActive || !currentStep) return

    const applyStepTarget = () => {
      if (currentStep.tabId) {
        if (window.location.pathname.startsWith('/admin')) {
          navigateToApp()
          return true
        }
        if (sidebarTabSetterRef.current) {
          setSidebarTab(currentStep.tabId)
          return true
        }
        return false
      } else if (currentStep.adminSection) {
        if (!window.location.pathname.startsWith('/admin')) {
          navigateToAdmin()
          return true
        }
        if (adminSectionSetterRef.current) {
          setAdminSection(currentStep.adminSection)
          return true
        }
        return false
      }
      return true
    }

    const applied = applyStepTarget()
    if (!applied) {
      // Retry on next tick — the target component may not have registered yet.
      applyAttemptRef.current += 1
    } else {
      applyAttemptRef.current = 0
    }
  }, [isTourActive, currentStep, setSidebarTab, setAdminSection, navigateToAdmin, navigateToApp, applyAttemptRef])

  // Retry mechanism: if the setter wasn't ready, poll a few times until it is.
  useEffect(() => {
    if (!isTourActive || !currentStep || applyAttemptRef.current === 0) return
    if (!currentStep.tabId && !currentStep.adminSection) return

    const maxAttempts = 5
    if (applyAttemptRef.current > maxAttempts) return

    const retryTimer = setTimeout(() => {
      if (currentStep.tabId && !window.location.pathname.startsWith('/admin')) {
        if (sidebarTabSetterRef.current) {
          setSidebarTab(currentStep.tabId)
          applyAttemptRef.current = 0
        } else {
          applyAttemptRef.current += 1
        }
      } else if (currentStep.adminSection && window.location.pathname.startsWith('/admin')) {
        if (adminSectionSetterRef.current) {
          setAdminSection(currentStep.adminSection)
          applyAttemptRef.current = 0
        } else {
          applyAttemptRef.current += 1
        }
      }
    }, 200)

    return () => clearTimeout(retryTimer)
  }, [isTourActive, currentStep, setSidebarTab, setAdminSection, applyAttemptRef])

  const value: TourContextValue = {
    isTourActive,
    currentStepIndex,
    currentStep,
    totalSteps: steps.length,
    startTour,
    nextStep,
    prevStep,
    skipTour,
    finishTour,
    setSidebarTab,
    setAdminSection,
    navigateToAdmin,
    navigateToApp,
    registerSidebarTabSetter,
    registerAdminSectionSetter,
  }

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>
}

export function useTour() {
  const ctx = useContext(TourContext)
  if (!ctx) throw new Error('useTour must be used within TourProvider')
  return ctx
}
